import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_IMAGE_HEIGHT,
  DEFAULT_Y_OFFSET,
} from "../components/SeatMap/seatPositions";
import type { SeatShape } from "../components/SeatMap/seatPositions";
import {
  PageContainer,
  PageContent,
  PageHeader,
} from "../components/layout/PageHeader";
import { Alert } from "../components/ui/Alert";
import { Breadcrumb } from "../components/ui/Breadcrumb";
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Input, Select } from "../components/ui/Input";
import { ConfirmModal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/Spinner";
import {
  type AdminSeat,
  useAdminSeats,
  useCreateSeat,
  useDeleteSeat,
  useUpdateSeatPosition,
} from "../hooks/api/useAdminSeats";
import { useLayout } from "../hooks/api/useLayout";

interface LocalSeat extends AdminSeat {
  localXPercent: number;
  localYPercent: number;
}

interface EditorSnapshot {
  localSeats: LocalSeat[];
  newSeatIds: Set<number>;
}

const MAX_UNDO_STACK = 50;

/** 0-100 の範囲にクランプ */
function clamp(value: number): number {
  return Math.max(0, Math.min(100, value));
}

/** 矢印キーから移動量を返す。矢印キー以外は null */
function getArrowKeyDelta(
  key: string,
  shiftKey: boolean
): { dx: number; dy: number } | null {
  const step = shiftKey ? 0.5 : 0.1;
  switch (key) {
    case "ArrowLeft":
      return { dx: -step, dy: 0 };
    case "ArrowRight":
      return { dx: step, dy: 0 };
    case "ArrowUp":
      return { dx: 0, dy: -step };
    case "ArrowDown":
      return { dx: 0, dy: step };
    default:
      return null;
  }
}

// 形状別のベースサイズ
const SEAT_DEFAULTS: Record<
  SeatShape,
  { widthPercent: number; heightPercent: number }
> = {
  tate: { widthPercent: 4, heightPercent: 6 },
  yoko: { widthPercent: 6, heightPercent: 4 },
  square: { widthPercent: 5, heightPercent: 5 },
};

async function createNewSeats(
  localSeats: LocalSeat[],
  newSeatIds: Set<number>,
  createSeat: ReturnType<typeof useCreateSeat>
) {
  const newSeatsToCreate = localSeats.filter((s) => newSeatIds.has(s.seatId));
  for (const seat of newSeatsToCreate) {
    await createSeat.mutateAsync({
      label: seat.label,
      xPercent: seat.localXPercent,
      yPercent: seat.localYPercent,
      widthPercent: seat.widthPercent,
      heightPercent: seat.heightPercent,
      shape: seat.shape,
      area: seat.area || undefined,
    });
  }
}

function applyPropertyEdit(
  field: "label" | "area" | "shape",
  value: string,
  setEditLabel: (v: string) => void,
  setEditArea: (v: string) => void,
  setEditShape: (v: SeatShape) => void
) {
  if (field === "label") {
    setEditLabel(value);
  } else if (field === "area") {
    setEditArea(value);
  } else if (field === "shape") {
    setEditShape(value as SeatShape);
  }
}

async function deleteLocalSeat(
  selectedSeatId: number,
  setLocalSeats: React.Dispatch<React.SetStateAction<LocalSeat[]>>,
  setNewSeatIds: React.Dispatch<React.SetStateAction<Set<number>>>,
  setSelectedSeatId: (id: number | null) => void,
  setShowDeleteModal: (v: boolean) => void
) {
  setLocalSeats((prev) => prev.filter((s) => s.seatId !== selectedSeatId));
  setNewSeatIds((prev) => {
    const next = new Set(prev);
    next.delete(selectedSeatId);
    return next;
  });
  setSelectedSeatId(null);
  setShowDeleteModal(false);
}

async function deleteExistingSeat(
  selectedSeatId: number,
  deleteSeat: ReturnType<typeof useDeleteSeat>,
  setLocalSeats: React.Dispatch<React.SetStateAction<LocalSeat[]>>,
  setSelectedSeatId: (id: number | null) => void,
  setShowDeleteModal: (v: boolean) => void,
  setSaveError: (v: string | null) => void
) {
  try {
    await deleteSeat.mutateAsync(selectedSeatId);
    setLocalSeats((prev) => prev.filter((s) => s.seatId !== selectedSeatId));
    setSelectedSeatId(null);
    setShowDeleteModal(false);
  } catch (err) {
    const message = err instanceof Error ? err.message : "削除に失敗しました";
    setSaveError(message);
  }
}

async function updateChangedSeats(
  changedSeats: LocalSeat[],
  newSeatIds: Set<number>,
  seats: AdminSeat[],
  updateSeatPosition: ReturnType<typeof useUpdateSeatPosition>
) {
  for (const seat of changedSeats) {
    if (newSeatIds.has(seat.seatId)) {
      continue;
    }
    const original = seats.find((s) => s.seatId === seat.seatId);
    await updateSeatPosition.mutateAsync({
      seatId: seat.seatId,
      xPercent: seat.localXPercent,
      yPercent: seat.localYPercent,
      ...(original && seat.label !== original.label && { label: seat.label }),
      ...(original &&
        seat.area !== original.area && {
          area: seat.area || undefined,
        }),
      ...(original && seat.shape !== original.shape && { shape: seat.shape }),
    });
  }
}

interface SeatOverlayItemProps {
  seat: LocalSeat;
  isSelected: boolean;
  isDragging: boolean;
  isChanged: boolean;
  scaledWidth: number;
  scaledHeight: number;
  onMouseDown: (e: React.MouseEvent, seatId: number) => void;
  onClick: (seatId: number) => void;
  setLocalSeats: React.Dispatch<React.SetStateAction<LocalSeat[]>>;
}

function SeatOverlayItem({
  seat,
  isSelected,
  isDragging,
  isChanged,
  scaledWidth,
  scaledHeight,
  onMouseDown,
  onClick,
  setLocalSeats,
}: SeatOverlayItemProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      onClick(seat.seatId);
    }
    if (!isSelected) {
      return;
    }
    const delta = getArrowKeyDelta(e.key, e.shiftKey);
    if (delta) {
      e.preventDefault();
      setLocalSeats((prev) =>
        prev.map((s) =>
          s.seatId === seat.seatId
            ? {
                ...s,
                localXPercent: clamp(s.localXPercent + delta.dx),
                localYPercent: clamp(s.localYPercent + delta.dy),
              }
            : s
        )
      );
    }
  };

  return (
    <div
      onMouseDown={(e) => onMouseDown(e, seat.seatId)}
      onClick={() => onClick(seat.seatId)}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`座席 ${seat.seatId < 0 ? "新規" : seat.seatId}${isSelected ? "（選択中、矢印キーで移動）" : ""}`}
      data-testid={`editor-seat-${seat.seatId}`}
      className={getSeatClassName(isSelected, isChanged, isDragging)}
      style={{
        left: `${seat.localXPercent}%`,
        top: `${seat.localYPercent}%`,
        width: `${scaledWidth}%`,
        height: `${scaledHeight}%`,
      }}
    >
      {seat.seatId < 0 ? "新規" : seat.seatId}
    </div>
  );
}

function buildSaveModalMessage(
  newCount: number,
  changedCount: number,
  saveError: string | null
): string {
  const newPart = newCount > 0 ? `${newCount}件の新規座席` : "";
  const separator = newCount > 0 && changedCount > 0 ? "と" : "";
  const changedPart = changedCount > 0 ? `${changedCount}件の座席変更` : "";
  const errorPart = saveError ? `\n\nエラー: ${saveError}` : "";
  return `${newPart}${separator}${changedPart}を保存しますか？${errorPart}`;
}

function buildDeleteModalMessage(selectedSeat: LocalSeat | null): string {
  if (!selectedSeat) {
    return "";
  }
  if (selectedSeat.seatId < 0) {
    return `新規座席「${selectedSeat.label}」を削除しますか？`;
  }
  return `座席 ${selectedSeat.seatId}「${selectedSeat.label}」を削除しますか？\n\n※この座席に関連する予約も削除されます。`;
}

function computeDragStart(
  container: HTMLDivElement | null,
  e: React.MouseEvent,
  seatId: number,
  localSeats: LocalSeat[]
): { offset: { x: number; y: number } } | null {
  if (!container) {
    return null;
  }
  const rect = container.getBoundingClientRect();
  const seat = localSeats.find((s) => s.seatId === seatId);
  if (!seat) {
    return null;
  }
  const mouseX = ((e.clientX - rect.left) / rect.width) * 100;
  const mouseY = ((e.clientY - rect.top) / rect.height) * 100;
  return {
    offset: {
      x: mouseX - seat.localXPercent,
      y: mouseY - seat.localYPercent,
    },
  };
}

function computeDragPosition(
  container: HTMLDivElement | null,
  e: React.MouseEvent,
  dragOffset: { x: number; y: number }
): { x: number; y: number } | null {
  if (!container) {
    return null;
  }
  const rect = container.getBoundingClientRect();
  const mouseX = ((e.clientX - rect.left) / rect.width) * 100;
  const mouseY = ((e.clientY - rect.top) / rect.height) * 100;
  return {
    x: Math.max(0, Math.min(100, mouseX - dragOffset.x)),
    y: Math.max(0, Math.min(100, mouseY - dragOffset.y)),
  };
}

function isUndoShortcut(e: KeyboardEvent): boolean {
  return (e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey;
}

function popUndoStack(
  prev: EditorSnapshot[],
  setLocalSeats: React.Dispatch<React.SetStateAction<LocalSeat[]>>,
  setNewSeatIds: React.Dispatch<React.SetStateAction<Set<number>>>
): EditorSnapshot[] {
  if (prev.length === 0) {
    return prev;
  }
  const next = [...prev];
  const snapshot = next.pop();
  if (snapshot) {
    setLocalSeats(snapshot.localSeats);
    setNewSeatIds(snapshot.newSeatIds);
  }
  return next;
}

function computeChangedSeats(
  localSeats: LocalSeat[],
  seats: AdminSeat[] | undefined
): LocalSeat[] {
  if (!seats) {
    return [];
  }
  return localSeats.filter((local) => {
    const original = seats.find((s) => s.seatId === local.seatId);
    if (!original) {
      return false;
    }
    return (
      local.localXPercent !== original.xPercent ||
      local.localYPercent !== original.yPercent ||
      local.label !== original.label ||
      local.area !== original.area ||
      local.shape !== original.shape
    );
  });
}

function shouldPushUndoAfterDrag(
  dragSeatId: number | null,
  preDragSnapshot: EditorSnapshot | null,
  localSeats: LocalSeat[]
): boolean {
  if (dragSeatId === null || !preDragSnapshot) {
    return false;
  }
  const pre = preDragSnapshot.localSeats.find((s) => s.seatId === dragSeatId);
  const post = localSeats.find((s) => s.seatId === dragSeatId);
  return !!(
    pre &&
    post &&
    (pre.localXPercent !== post.localXPercent ||
      pre.localYPercent !== post.localYPercent)
  );
}

interface SeatPropertyPanelProps {
  selectedSeat: LocalSeat | null;
  editLabel: string;
  editArea: string;
  editShape: SeatShape;
  changedSeats: LocalSeat[];
  onUpdateProperty: (field: "label" | "area" | "shape", value: string) => void;
  onDeleteClick: () => void;
}

function SeatPropertyPanel({
  selectedSeat,
  editLabel,
  editArea,
  editShape,
  changedSeats,
  onUpdateProperty,
  onDeleteClick,
}: SeatPropertyPanelProps) {
  if (!selectedSeat) {
    return (
      <p className="text-sm text-gray-500">
        座席をクリックして選択してください
      </p>
    );
  }

  const seatIdDisplay = selectedSeat.seatId < 0 ? "新規" : selectedSeat.seatId;
  const isChanged = changedSeats.some((c) => c.seatId === selectedSeat.seatId);

  return (
    <div className="space-y-3">
      <div>
        <span className="text-xs text-gray-500">座席ID</span>
        <p className="font-medium">{seatIdDisplay}</p>
      </div>
      <Input
        label="ラベル"
        value={editLabel}
        onChange={(e) => onUpdateProperty("label", e.target.value)}
        maxLength={16}
        placeholder="例: A-1, 窓際席"
        data-testid="seat-label-input"
      />
      <Input
        label="エリア"
        value={editArea}
        onChange={(e) => onUpdateProperty("area", e.target.value)}
        maxLength={32}
        placeholder="例: 東エリア"
        data-testid="seat-area-input"
      />
      <Select
        label="形状"
        value={editShape}
        onChange={(e) => onUpdateProperty("shape", e.target.value)}
        options={[
          { value: "tate", label: "縦長" },
          { value: "yoko", label: "横長" },
          { value: "square", label: "正方形" },
        ]}
        data-testid="seat-shape-select"
      />
      <hr />
      <div>
        <span className="text-xs text-gray-500">X座標 (%)</span>
        <p className="font-mono">{selectedSeat.localXPercent.toFixed(2)}%</p>
      </div>
      <div>
        <span className="text-xs text-gray-500">Y座標 (%)</span>
        <p className="font-mono">{selectedSeat.localYPercent.toFixed(2)}%</p>
      </div>
      {isChanged && (
        <Alert variant="warning" className="text-xs">
          この座席は変更されています
        </Alert>
      )}
      <hr />
      <Button
        variant="danger"
        onClick={onDeleteClick}
        className="w-full"
        data-testid="delete-seat-button"
      >
        この座席を削除
      </Button>
    </div>
  );
}

function getSeatClassName(
  isSelected: boolean,
  isChanged: boolean,
  isDragging: boolean
): string {
  const dragClass = isDragging ? "cursor-grabbing z-50" : "cursor-grab";
  const colorClass = isSelected
    ? "border-primary-500 bg-primary-500/70 text-white ring-2 ring-primary-300"
    : isChanged
      ? "border-amber-500 bg-amber-500/50 text-amber-900"
      : "border-gray-500 bg-gray-300/70 text-gray-800";
  return `absolute border-2 rounded flex items-center justify-center text-[10px] font-bold transition-colors duration-150 select-none ${dragClass} ${colorClass} hover:bg-primary-400/60`;
}

export function AdminSeatsPage() {
  const {
    data: seats,
    isLoading: seatsLoading,
    error: seatsError,
    refetch: refetchSeats,
  } = useAdminSeats();
  const {
    data: layout,
    isLoading: layoutLoading,
    error: layoutError,
  } = useLayout();
  const updateSeatPosition = useUpdateSeatPosition();
  const createSeat = useCreateSeat();
  const deleteSeat = useDeleteSeat();

  // レイアウト情報から計算
  const imageUrl = layout?.imageUrl ?? "";
  const imageHeight = layout?.originalHeight ?? DEFAULT_IMAGE_HEIGHT;
  const yOffset = layout?.yOffset ?? DEFAULT_Y_OFFSET;
  const aspectRatio = layout?.aspectRatio ?? 1;

  const isLoading = seatsLoading || layoutLoading;
  // エラーハンドリングは将来追加予定
  void seatsError;
  void layoutError;

  // ローカル編集状態
  const [localSeats, setLocalSeats] = useState<LocalSeat[]>([]);
  const [selectedSeatId, setSelectedSeatId] = useState<number | null>(null);
  const [globalScale, setGlobalScale] = useState(1.0);
  const [isDirty, setIsDirty] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // プロパティ編集状態
  const [editLabel, setEditLabel] = useState("");
  const [editArea, setEditArea] = useState("");
  const [editShape, setEditShape] = useState<SeatShape>("square");

  // 新規座席追加中フラグ（ローカル追加後、保存されるまでの座席）
  const [newSeatIds, setNewSeatIds] = useState<Set<number>>(new Set());
  // 次のローカルID（マイナス値で管理）
  const [nextLocalId, setNextLocalId] = useState(-1);

  // Undo スタック
  const [undoStack, setUndoStack] = useState<EditorSnapshot[]>([]);
  const preDragSnapshotRef = useRef<EditorSnapshot | null>(null);

  // ドラッグ状態
  const [dragSeatId, setDragSeatId] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // サーバーデータをローカル状態に同期
  useEffect(() => {
    if (seats && localSeats.length === 0) {
      setLocalSeats(
        seats.map((seat) => ({
          ...seat,
          localXPercent: seat.xPercent ?? 0,
          localYPercent: seat.yPercent ?? 0,
        }))
      );
    }
  }, [seats, localSeats.length]);

  const changedSeats = useMemo(
    () => computeChangedSeats(localSeats, seats),
    [localSeats, seats]
  );

  useEffect(() => {
    // 変更された座席または新規座席があればdirty
    setIsDirty(changedSeats.length > 0 || newSeatIds.size > 0);
  }, [changedSeats, newSeatIds]);

  // Undo: スナップショットをスタックにプッシュ
  const pushUndo = useCallback((snapshot: EditorSnapshot) => {
    setUndoStack((prev) => {
      const next = [...prev, snapshot];
      return next.length > MAX_UNDO_STACK ? next.slice(-MAX_UNDO_STACK) : next;
    });
  }, []);

  const handleUndo = useCallback(() => {
    setUndoStack((prev) => popUndoStack(prev, setLocalSeats, setNewSeatIds));
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isUndoShortcut(e)) {
        e.preventDefault();
        handleUndo();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [handleUndo]);

  // 選択座席の情報
  const selectedSeat = localSeats.find((s) => s.seatId === selectedSeatId);

  // 選択座席が変わったときにプロパティ編集状態を更新
  useEffect(() => {
    if (selectedSeat) {
      setEditLabel(selectedSeat.label);
      setEditArea(selectedSeat.area ?? "");
      setEditShape(selectedSeat.shape ?? "square");
    }
  }, [selectedSeat]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, seatId: number) => {
      e.preventDefault();
      const result = computeDragStart(
        containerRef.current,
        e,
        seatId,
        localSeats
      );
      if (!result) {
        return;
      }

      preDragSnapshotRef.current = {
        localSeats: localSeats.map((s) => ({ ...s })),
        newSeatIds: new Set(newSeatIds),
      };

      setDragOffset(result.offset);
      setDragSeatId(seatId);
      setSelectedSeatId(seatId);
    },
    [localSeats, newSeatIds]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (dragSeatId === null) {
        return;
      }

      const position = computeDragPosition(containerRef.current, e, dragOffset);
      if (!position) {
        return;
      }

      setLocalSeats((prev) =>
        prev.map((s) =>
          s.seatId === dragSeatId
            ? { ...s, localXPercent: position.x, localYPercent: position.y }
            : s
        )
      );
    },
    [dragSeatId, dragOffset]
  );

  const handleMouseUp = useCallback(() => {
    if (
      shouldPushUndoAfterDrag(
        dragSeatId,
        preDragSnapshotRef.current,
        localSeats
      ) &&
      preDragSnapshotRef.current
    ) {
      pushUndo(preDragSnapshotRef.current);
    }
    preDragSnapshotRef.current = null;
    setDragSeatId(null);
  }, [dragSeatId, localSeats, pushUndo]);

  // 座席クリック
  const handleSeatClick = (seatId: number) => {
    setSelectedSeatId(seatId);
  };

  // リセット
  const handleReset = () => {
    if (seats) {
      setLocalSeats(
        seats.map((seat) => ({
          ...seat,
          localXPercent: seat.xPercent ?? 0,
          localYPercent: seat.yPercent ?? 0,
        }))
      );
      setSelectedSeatId(null);
      setNewSeatIds(new Set());
      setUndoStack([]);
    }
  };

  // 新規座席追加
  const handleAddSeat = (shape: SeatShape) => {
    // Undo: 追加前のスナップショットを保存
    pushUndo({
      localSeats: localSeats.map((s) => ({ ...s })),
      newSeatIds: new Set(newSeatIds),
    });

    const localId = nextLocalId;
    setNextLocalId((prev) => prev - 1);

    const defaults = SEAT_DEFAULTS[shape];

    // 全体サイズスライダーの値を適用
    // heightPercent はコンテナ高さ基準なので、aspectRatio で補正して
    // 視覚的に意図した比率にする（例: square なら正方形に見える）
    const scaledWidth = defaults.widthPercent * globalScale;
    const scaledHeight = defaults.heightPercent * globalScale * aspectRatio;

    const newSeat: LocalSeat = {
      seatId: localId,
      label: "新規座席",
      isActive: true,
      xPercent: 50,
      yPercent: 50,
      widthPercent: scaledWidth,
      heightPercent: scaledHeight,
      shape: shape,
      area: "",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      localXPercent: 50,
      localYPercent: 50,
    };

    setLocalSeats((prev) => [...prev, newSeat]);
    setNewSeatIds((prev) => new Set([...prev, localId]));
    setSelectedSeatId(localId);
    setIsDirty(true);
  };

  // プロパティ更新（ローカル）
  const handleUpdateProperty = (
    field: "label" | "area" | "shape",
    value: string
  ) => {
    if (!selectedSeatId) {
      return;
    }

    applyPropertyEdit(field, value, setEditLabel, setEditArea, setEditShape);

    setLocalSeats((prev) =>
      prev.map((s) =>
        s.seatId === selectedSeatId ? { ...s, [field]: value } : s
      )
    );
    setIsDirty(true);
  };

  // 削除確認モーダルを開く
  const handleDeleteClick = () => {
    if (selectedSeatId) {
      setShowDeleteModal(true);
    }
  };

  // 削除実行
  const handleDeleteConfirm = async () => {
    if (!selectedSeatId) {
      return;
    }

    pushUndo({
      localSeats: localSeats.map((s) => ({ ...s })),
      newSeatIds: new Set(newSeatIds),
    });

    if (selectedSeatId < 0) {
      await deleteLocalSeat(
        selectedSeatId,
        setLocalSeats,
        setNewSeatIds,
        setSelectedSeatId,
        setShowDeleteModal
      );
      return;
    }

    await deleteExistingSeat(
      selectedSeatId,
      deleteSeat,
      setLocalSeats,
      setSelectedSeatId,
      setShowDeleteModal,
      setSaveError
    );
  };

  // 保存
  const handleSaveClick = () => {
    if (isDirty) {
      setShowSaveModal(true);
      setSaveError(null);
    }
  };

  const handleSaveConfirm = async () => {
    try {
      await createNewSeats(localSeats, newSeatIds, createSeat);
      await updateChangedSeats(
        changedSeats,
        newSeatIds,
        seats ?? [],
        updateSeatPosition
      );

      setShowSaveModal(false);
      await refetchSeats();
      setLocalSeats([]);
      setNewSeatIds(new Set());
      setSelectedSeatId(null);
      setUndoStack([]);
    } catch (err) {
      const message = err instanceof Error ? err.message : "保存に失敗しました";
      setSaveError(message);
    }
  };

  const saveModalMessage = buildSaveModalMessage(
    newSeatIds.size,
    changedSeats.length,
    saveError
  );
  const deleteModalMessage = buildDeleteModalMessage(selectedSeat ?? null);

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader />
        <PageContent>
          <div
            className="flex justify-center items-center py-12"
            role="status"
            aria-live="polite"
          >
            <Spinner size="lg" />
            <span className="ml-3 text-gray-600">読み込み中...</span>
          </div>
        </PageContent>
      </PageContainer>
    );
  }

  // エラー
  if (layoutError) {
    return (
      <PageContainer>
        <PageHeader />
        <PageContent>
          <Alert variant="warning">
            見取り図が設定されていません。先に見取り図を設定してください。
          </Alert>
        </PageContent>
      </PageContainer>
    );
  }

  if (seatsError) {
    return (
      <PageContainer>
        <PageHeader />
        <PageContent>
          <Alert variant="error">
            座席データの取得に失敗しました。再読み込みしてください。
          </Alert>
        </PageContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader />
      <PageContent>
        <h1 className="sr-only">座席配置エディタ</h1>
        <Breadcrumb
          items={[
            { label: "管理", to: "/admin/seats" },
            { label: "座席配置エディタ" },
          ]}
        />
        <div className="space-y-6">
          {/* 操作パネル */}
          <Card>
            <CardHeader title="座席配置設定" />
            <CardContent>
              <div className="flex flex-wrap items-center gap-6">
                {/* サイズ調整スライダー */}
                <div className="flex items-center gap-3">
                  <label
                    htmlFor="scale-slider"
                    className="text-sm font-medium text-gray-700"
                  >
                    全体サイズ: {Math.round(globalScale * 100)}%
                  </label>
                  <input
                    id="scale-slider"
                    type="range"
                    min="0.5"
                    max="1.5"
                    step="0.1"
                    value={globalScale}
                    onChange={(e) => setGlobalScale(Number(e.target.value))}
                    className="w-32"
                    data-testid="scale-slider"
                  />
                </div>

                {/* 変更数表示 */}
                {isDirty && (
                  <span className="text-sm text-amber-600">
                    {changedSeats.length + newSeatIds.size}件の変更があります
                  </span>
                )}

                {/* ボタン */}
                <div className="flex gap-2 ml-auto">
                  {/* 座席追加ボタン群 */}
                  <div className="flex gap-1">
                    <Button
                      variant="secondary"
                      onClick={() => handleAddSeat("tate")}
                      data-testid="add-seat-tate-button"
                      title="縦長座席を追加"
                    >
                      + 縦
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => handleAddSeat("yoko")}
                      data-testid="add-seat-yoko-button"
                      title="横長座席を追加"
                    >
                      + 横
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => handleAddSeat("square")}
                      data-testid="add-seat-square-button"
                      title="正方形座席を追加"
                    >
                      + □
                    </Button>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={handleUndo}
                    disabled={undoStack.length === 0}
                    data-testid="undo-button"
                    title="元に戻す (Ctrl+Z)"
                  >
                    元に戻す
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleReset}
                    disabled={!isDirty}
                    data-testid="reset-button"
                  >
                    リセット
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSaveClick}
                    disabled={!isDirty}
                    isLoading={updateSeatPosition.isPending}
                    data-testid="save-button"
                  >
                    保存
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 座席マップエディタ */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* 座席マップ */}
            <div className="flex-1 min-w-0">
              <Card>
                <CardHeader title="座席マップ（ドラッグで移動）" />
                <CardContent>
                  <div
                    ref={containerRef}
                    className="relative w-full overflow-hidden cursor-crosshair"
                    style={{
                      aspectRatio: `${aspectRatio}`,
                    }}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    role="application"
                    aria-label="座席配置エディタ"
                    data-testid="seat-editor-map"
                  >
                    {/* 背景画像 */}
                    <img
                      src={imageUrl}
                      alt="オフィスレイアウト"
                      className="absolute inset-0 w-full h-full object-cover object-bottom"
                      onLoad={() => setImageLoaded(true)}
                      style={{
                        objectPosition: `center ${(yOffset / imageHeight) * 100}%`,
                      }}
                    />

                    {/* 座席オーバーレイ */}
                    {imageLoaded &&
                      localSeats.map((seat) => {
                        const isSelected = seat.seatId === selectedSeatId;
                        const isDragging = seat.seatId === dragSeatId;
                        const isChanged = changedSeats.some(
                          (c) => c.seatId === seat.seatId
                        );
                        const scaledWidth =
                          (seat.widthPercent ?? 5) * globalScale;
                        const scaledHeight =
                          (seat.heightPercent ?? 5) * globalScale;

                        return (
                          <SeatOverlayItem
                            key={seat.seatId}
                            seat={seat}
                            isSelected={isSelected}
                            isDragging={isDragging}
                            isChanged={isChanged}
                            scaledWidth={scaledWidth}
                            scaledHeight={scaledHeight}
                            onMouseDown={handleMouseDown}
                            onClick={handleSeatClick}
                            setLocalSeats={setLocalSeats}
                          />
                        );
                      })}
                  </div>

                  <p className="mt-2 text-xs text-gray-500">
                    ヒント:
                    座席をドラッグして位置を調整できます。選択中の座席は矢印キーで微調整（Shift+矢印で大きく移動）できます。
                    変更した座席はオレンジ色で表示されます。
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* 選択座席情報 */}
            <div className="w-full lg:w-72 lg:flex-shrink-0">
              <Card>
                <CardHeader title="選択座席情報" />
                <CardContent>
                  <SeatPropertyPanel
                    selectedSeat={selectedSeat ?? null}
                    editLabel={editLabel}
                    editArea={editArea}
                    editShape={editShape}
                    changedSeats={changedSeats}
                    onUpdateProperty={handleUpdateProperty}
                    onDeleteClick={handleDeleteClick}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </PageContent>

      {/* 保存確認モーダル */}
      <ConfirmModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onConfirm={handleSaveConfirm}
        title="変更を保存"
        message={saveModalMessage}
        confirmText="保存"
        variant="primary"
        isLoading={updateSeatPosition.isPending}
      />

      {/* 削除確認モーダル */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="座席を削除"
        message={deleteModalMessage}
        confirmText="削除"
        variant="danger"
        isLoading={deleteSeat.isPending}
      />
    </PageContainer>
  );
}
